import ms from 'milliseconds'
import { isEmpty, isObject } from 'lodash-es'
import { createSelector } from 'reselect'
import {
  appTimeSelector,
  configSelector,
} from '../../../shared/app/shell/index.js'
import shouldUpdate from '../../../shared/app/stale-reducers/should-update.js'
import {
  userEmailSelector,
  userMfaPhoneSelector,
  hasUnverifiedPhoneSelector,
  hasVerifiedPhoneSelector,
} from '../../../shared/app/bundles/user/index.js'
import {
  localeMailTopicsSelector,
  localeEmailTopicsSelector,
} from '../../../shared/app/state/selectors/communication-preferences.js'
import { signedInSelector } from '../../../shared/app/bundles/user/state/selectors/profile.js'

const settingsSelector = (state) => state.accountSettings

const rawCommunicationPreferencesSelector = (state) =>
  state.accountSettings.communicationPreferences

const enterEmailCodeSelector = (state) => state?.accountSettings?.enterEmailCode

const mfaPhoneSelector = createSelector(
  settingsSelector,
  ({ mfaPhone }) => mfaPhone
)

// The phone # that initiated the flow to be added but hasn't been completed yet - if one exists.
export const currentAddPhoneSelector = createSelector(
  mfaPhoneSelector,
  ({ addPhone }) => addPhone || {}
)

// The phone # that initiated the flow to be modified but hasn't been completed yet.
export const currentEditPhoneSelector = createSelector(
  mfaPhoneSelector,
  ({ editPhone }) => editPhone?.phonePendingVerification ?? editPhone ?? {}
)

export const isMfaFormForAddingSelector = createSelector(
  hasVerifiedPhoneSelector,
  hasUnverifiedPhoneSelector,
  (hasVerifiedPhone, hasUnverifiedPhone) =>
    !hasVerifiedPhone && !hasUnverifiedPhone
)

export const communicationPreferencesSelector = (state) =>
  state.accountSettings?.communicationPreferences?.data

export const isMfaPhoneFormLoadingSelector = createSelector(
  currentAddPhoneSelector,
  currentEditPhoneSelector,
  (addPhone, editPhone) => Boolean(addPhone.loading || editPhone.loading)
)

export const mfaDisplayedPhoneSelector = createSelector(
  userMfaPhoneSelector,
  (phone) => {
    if (phone.isVerified) {
      return phone.phoneNumber
    }

    return null
  }
)

export const isCommunicationPreferencesObjectEmptySelector = createSelector(
  communicationPreferencesSelector,
  (communicationPreferences) =>
    isObject(communicationPreferences) && isEmpty(communicationPreferences)
)

const passwordSelector = createSelector(
  settingsSelector,
  (settings) => settings.password
)

export const shouldFetchCommunicationPreferencesSelector = createSelector(
  signedInSelector,
  userEmailSelector,
  appTimeSelector,
  rawCommunicationPreferencesSelector,
  (signedIn, userEmail, time, communicationPreferences) => {
    if (
      signedIn &&
      userEmail &&
      shouldUpdate(communicationPreferences, {
        staleTime: ms.minutes(5),
        now: time,
      })
    ) {
      return true
    }

    return null
  }
)

export const isSavingPasswordSelector = createSelector(
  passwordSelector,
  (password) => Boolean(password.loading)
)

export const showGenericPasswordErrorSelector = createSelector(
  passwordSelector,
  (password) => Boolean(password.showGenericError)
)

export const localeSupportsEmailTopicsSelector = createSelector(
  localeEmailTopicsSelector,
  (localeEmailTopics) => localeEmailTopics && localeEmailTopics.length > 0
)

export const localeSupportsMailTopicsSelector = createSelector(
  localeMailTopicsSelector,
  (localeMailTopics) => localeMailTopics && localeMailTopics.length > 0
)

export const localeSupportsMfaSelector = createSelector(
  configSelector,
  (config) => Boolean(config?.mfa?.enabled)
)

export const proposedNewEmailSelector = createSelector(
  settingsSelector,
  (settings) => settings?.editEmail?.proposedNewEmail
)

export const isRequestingEmailValidationCodeSelector = createSelector(
  settingsSelector,
  (settings) => Boolean(settings?.editEmail?.loading)
)

export const expectingConfirmEmailCodeResponseSelector = createSelector(
  enterEmailCodeSelector,
  (enterEmailCode) => Boolean(enterEmailCode?.confirmEmailCode?.loading)
)

export const accountDeleteOneTrustFormUrlSelector = createSelector(
  configSelector,
  (config) => config?.accountDeleteOneTrustFormUrl
)

export const emailLanguagePreferenceSelector = createSelector(
  settingsSelector,
  (settings) => settings?.languagePreferences?.data ?? 'en-CA'
)
